import React, { useState } from 'react';
import { Loader2, Plus, X, Save, Trash2, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { COMIDAS_DEL_SUPLEMENTO, dondeSale, noSaleEnNinguna } from '../../lib/comidaDelSuplemento';
import { sexoLabel, suplementoCatLabel } from '../../lib/labels';
import { hoyEnEspana, pintarDia } from '../../lib/horaEspana';
import SuplementoDelCatalogo from './SuplementoDelCatalogo';
import {
    descartados, buscarEnElCatalogo, fechaDeLaSiguiente, fechaDelGuardado, PLANTILLAS_DE_NOTA,
} from '../../lib/suplementosDelCliente';

// LA PESTAÑA SUPLEMENTACIÓN DE LA FICHA, COMO LA DE CALMA (Jesús, 20-10).
//
// Arriba el historial de versiones; debajo, a la izquierda lo que el cliente NO lleva (el
// catálogo menos lo suyo) y a la derecha lo que lleva, con el botón de guardar para una
// fecha; y la nota con sus plantillas. El estado y las llamadas siguen en la ficha
// (ClientDetailPage): aquí solo se pinta y se avisa de lo que se toca. El contrato con el
// servidor no cambia: se guardan los dos bloques, el que toma ahora y el siguiente, cada
// uno con su fecha.

const fecha = (dia) => pintarDia(dia, undefined, '-');

const BLOQUES = [
    ['actual', 'Lo que toma ahora'],
    ['siguiente', 'La siguiente versión'],
];

const SuplementosDelCliente = ({
    protocolo, setProtocolo, catalogo, guardando,
    onGuardar, onAnadir, onQuitar, onEditar, onBorrarVersion,
}) => {
    // Por defecto se prepara la SIGUIENTE versión, como en Calma: «Guardar suplementos para»
    // la fecha de la que ya hay preparada o, si no, el lunes que viene.
    const [bloque, setBloque] = useState('siguiente');
    const [filtro, setFiltro] = useState('');
    // EL CATÁLOGO SIN SALIR DE LA FICHA (Francisco, 20-10): plegado y solo para verlo; se
    // gestiona en /admin/supplements-catalog, que pinta las mismas tarjetas.
    const [verCatalogo, setVerCatalogo] = useState(false);
    const [buscaCatalogo, setBuscaCatalogo] = useState('');

    const lista = protocolo[bloque] || [];
    const libres = descartados(catalogo, lista, filtro);
    const fechaGuardado = fechaDelGuardado(bloque, protocolo);
    // El día de España, como el servidor, que es quien pone en vigor cada versión.
    const hoy = hoyEnEspana();

    const anadir = (c) => { onAnadir(bloque, c.id); setFiltro(''); };
    const cambiarFecha = (valor) => setProtocolo(p => ({ ...p, [bloque === 'actual' ? 'actual_fecha' : 'siguiente_fecha']: valor }));
    const partirDeLoActual = () => setProtocolo(p => ({ ...p, siguiente: (p.actual || []).map(it => ({ ...it })) }));
    // La siguiente se guarda con la fecha que se VE: si no tenía, la del lunes que viene. Se
    // manda la mire quien la mire: guardando desde «Lo que toma ahora», una siguiente sin
    // fecha la descartaba el servidor (revisor, 20-10).
    const guardar = () => onGuardar((protocolo.siguiente || []).length
        ? { siguiente_fecha: fechaDelGuardado('siguiente', protocolo) } : {});

    return (
        <div className="space-y-4" data-testid="sup-pestana">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-[#6F6B64] uppercase tracking-wider">Protocolo de suplementación</p>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setVerCatalogo(v => !v)} aria-expanded={verCatalogo}
                        className="bg-transparent border-[#D6D2C9] text-[#1C1B19]" data-testid="sup-ver-catalogo">
                        <BookOpen className="w-3 h-3 mr-1 text-[#B43E06]" />{verCatalogo ? 'Ocultar catálogo' : 'Ver catálogo'}
                    </Button>
                </div>
            </div>

            {verCatalogo && (
                <div className="space-y-2" data-testid="sup-catalogo">
                    <Input value={buscaCatalogo} onChange={e => setBuscaCatalogo(e.target.value)} placeholder="Buscar en el catálogo"
                        aria-label="Buscar en el catálogo" data-testid="sup-catalogo-buscar"
                        className="bg-white border-[#D6D2C9] text-[#1C1B19] text-sm h-9" />
                    {(() => {
                        const fichas = buscarEnElCatalogo(catalogo, buscaCatalogo);
                        return fichas.length === 0
                            ? <p className="text-[#6F6B64] text-sm">{buscaCatalogo ? 'Nada con esa búsqueda.' : 'El catálogo está vacío.'}</p>
                            : <div className="grid md:grid-cols-2 gap-2 max-h-[480px] overflow-y-auto pr-1">
                                {fichas.map(c => <SuplementoDelCatalogo key={c.id} it={c} claro />)}
                            </div>;
                    })()}
                </div>
            )}

            {/* EL HISTÓRICO (punto 33), en el recuadro amarillo de Calma. Antes no quedaba
                registro de qué tomaba el cliente en cada momento. */}
            {protocolo.versiones?.length > 0 && (
                <div className="rounded-lg border border-[#F1DFA0] bg-[#FEF9E7] p-3 space-y-1.5" data-testid="sup-historial">
                    <p className="text-xs font-semibold text-[#854D0E] uppercase tracking-wider">Historial de versiones ({protocolo.versiones.length})</p>
                    {[...protocolo.versiones].reverse().map(v => {
                        const loTomaAhora = v.fecha === protocolo.actual_fecha;
                        const preparada = v.fecha > hoy;
                        return (
                            <div key={v.fecha} className="flex items-start gap-2 text-sm" data-testid={`sup-version-${v.fecha}`}>
                                <div className="flex-1 min-w-0 text-[#1C1B19]">
                                    <b className="tabular-nums text-[#1C1B19]">{fecha(v.fecha)}</b>
                                    {loTomaAhora && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-[#B43E06]">lo toma ahora</span>}
                                    {preparada && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-[#854D0E]">preparada</span>}
                                    <span className="text-[#57544E]">: {(v.items || []).map(i => i.titulo).join(', ') || 'sin suplementos'}</span>
                                    {v.nota && <p className="text-[#57544E] text-[11px] mt-0.5 truncate" title={v.nota}>{v.nota}</p>}
                                    {v.guardado_por && <p className="text-[#6F6B64] text-[10px]">lo puso {v.guardado_por}</p>}
                                </div>
                                <button onClick={() => onBorrarVersion(v.fecha)} title="Borrar esta versión"
                                    className="flex-shrink-0 inline-flex items-center gap-1 rounded bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[11px] px-2 py-0.5"
                                    data-testid={`sup-borrar-${v.fecha}`}>
                                    <Trash2 className="w-3 h-3" />borrar
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Qué versión se prepara. Las dos se guardan juntas, como siempre; esto solo dice
                cuál se ve en las columnas y para qué fecha es el botón. */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[#6F6B64] text-xs">Preparando:</span>
                {BLOQUES.map(([b, titulo]) => {
                    const dia = b === 'actual' ? protocolo.actual_fecha : protocolo.siguiente_fecha;
                    return (
                        <button key={b} onClick={() => setBloque(b)} data-testid={`sup-bloque-${b}`}
                            className={`text-xs rounded-full px-3 py-1 border ${bloque === b
                                ? 'border-[#FF671F] bg-[#FDEBDF] text-[#1C1B19]'
                                : 'border-[#D6D2C9] text-[#57544E] hover:text-[#1C1B19]'}`}>
                            {titulo}{dia ? ` (desde el ${fecha(dia)})` : b === 'siguiente' ? ' (nueva)' : ''}
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {/* IZQUIERDA: el catálogo menos lo que ya lleva. */}
                <Card className="bg-white border-[#E5E2DB]" data-testid="sup-descartados">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">Suplementos descartados</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Label htmlFor="sup-filtro" className="text-[#57544E] text-xs shrink-0">Filtro:</Label>
                            <Input id="sup-filtro" value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Escribe para filtrar"
                                data-testid="sup-filtro" className="bg-white border-[#D6D2C9] text-[#1C1B19] text-sm h-8" />
                        </div>
                        <div className="max-h-[480px] overflow-y-auto space-y-1 pr-1">
                            {libres.length === 0 && <p className="text-[#6F6B64] text-sm py-2">{filtro ? 'Nada con ese filtro.' : 'Lleva todo el catálogo.'}</p>}
                            {libres.map(c => (
                                <div key={c.id} className="flex items-center justify-between gap-2 p-2 bg-[#F6F5F2] rounded-lg border border-[#E5E2DB]">
                                    <div className="min-w-0">
                                        <p className="text-[#1C1B19] text-sm truncate">{c.titulo}</p>
                                        <p className="text-[#6F6B64] text-[11px] truncate">
                                            {suplementoCatLabel(c.categoria)}{c.sexo && c.sexo !== 'ambos' ? ` · ${sexoLabel(c.sexo)}` : ''}
                                        </p>
                                    </div>
                                    <button onClick={() => anadir(c)} data-testid={`sup-anadir-${c.id}`}
                                        className="flex-shrink-0 rounded bg-[#15803D] hover:bg-[#166534] text-white text-xs px-2.5 py-1">
                                        añadir
                                    </button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* DERECHA: lo que lleva, editable, y el guardado con su fecha. */}
                <Card className="bg-white border-[#E5E2DB]" data-testid="sup-para-el-cliente">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-[#6F6B64] uppercase tracking-wider">Suplementos para el cliente</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {lista.length === 0 && (
                            <div className="text-[#6F6B64] text-sm space-y-2">
                                <p>{bloque === 'siguiente' ? 'Todavía no hay una versión siguiente.' : 'Sin suplementos.'}</p>
                                {bloque === 'siguiente' && protocolo.actual?.length > 0 && (
                                    <Button size="sm" variant="outline" onClick={partirDeLoActual} className="bg-transparent border-[#D6D2C9] text-[#1C1B19] h-7 px-2 text-xs" data-testid="sup-partir-de-lo-actual">
                                        Partir de lo que toma ahora
                                    </Button>
                                )}
                            </div>
                        )}
                        {/* La dosis, el momento y las observaciones se EDITAN aquí aunque el
                            suplemento venga del catálogo (2.7). El título se queda fijo para
                            que siga correspondiendo con su ficha del catálogo. */}
                        {lista.map((it, i) => (
                            <div key={`${it.catalog_id || it.titulo}-${i}`} className="p-2.5 bg-[#F6F5F2] rounded-lg border border-[#E5E2DB]">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-[#1C1B19] text-sm font-medium min-w-0">{it.titulo}</p>
                                    <button onClick={() => onQuitar(bloque, i)} data-testid={`sup-quitar-${bloque}-${i}`}
                                        className="flex-shrink-0 rounded bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs px-2.5 py-1">
                                        quitar
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                    <Input value={it.cuanto || ''} onChange={e => onEditar(bloque, i, 'cuanto', e.target.value)}
                                        placeholder="Cuánto (ej: 1 cápsula)" data-testid={`sup-cuanto-${bloque}-${i}`}
                                        className="bg-white border-[#D6D2C9] text-[#1C1B19] text-xs h-8" />
                                    <Input value={it.cuando || ''} onChange={e => onEditar(bloque, i, 'cuando', e.target.value)}
                                        placeholder="Cuándo (ej: con el desayuno)" data-testid={`sup-cuando-${bloque}-${i}`}
                                        className="bg-white border-[#D6D2C9] text-[#1C1B19] text-xs h-8" />
                                </div>
                                <Input value={it.observaciones || ''} onChange={e => onEditar(bloque, i, 'observaciones', e.target.value)}
                                    placeholder="Observaciones (opcional)" data-testid={`sup-obs-${bloque}-${i}`}
                                    className="bg-white border-[#D6D2C9] text-[#1C1B19] text-xs h-8 mt-2" />
                                {/* DÓNDE SALE EN SU INICIO, ELEGIDO AQUÍ (27-08), por cliente. La
                                    frase de al lado la calcula el SERVIDOR (`en_comidas`) y es la
                                    misma que ve el cliente; al tocar el desplegable o el «Cuándo»
                                    se queda vieja, y se dice. */}
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <span className="text-[#6F6B64] text-[11px] shrink-0">En su Inicio sale con</span>
                                    <select value={it.comida || ''} onChange={e => onEditar(bloque, i, 'comida', e.target.value)}
                                        data-testid={`sup-comida-${bloque}-${i}`}
                                        className="bg-white border border-[#D6D2C9] rounded-md text-[#1C1B19] text-xs h-8 px-2">
                                        {COMIDAS_DEL_SUPLEMENTO.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                                    </select>
                                    {it.en_comidas !== undefined && (
                                        <span className={`text-[11px] ${noSaleEnNinguna(it.en_comidas) ? 'text-[#854D0E]' : 'text-[#57544E]'}`}
                                            data-testid={`sup-donde-${bloque}-${i}`}>
                                            {dondeSale(it.en_comidas)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Cada bloque es una VERSIÓN con su fecha (punto 33). Cambiar una dosis
                            de lo que toma ahora corrige esa versión y no abre otra: abrir una
                            versión nueva es una decisión, y para eso está la fecha. */}
                        <div className="pt-2 border-t border-[#E5E2DB] space-y-2">
                            <div>
                                <Label className="text-[#6F6B64] text-xs">{bloque === 'actual' ? 'Lo toma desde el día' : 'A partir del día'}</Label>
                                <Input type="date"
                                    value={bloque === 'actual' ? (protocolo.actual_fecha || '') : fechaDeLaSiguiente(protocolo.siguiente_fecha)}
                                    onChange={e => cambiarFecha(e.target.value)}
                                    data-testid={`sup-fecha-${bloque}`}
                                    className="bg-white border-[#D6D2C9] text-[#1C1B19] mt-1 w-full sm:w-48" />
                            </div>
                            <Button onClick={guardar} disabled={guardando} data-testid="save-supplements-btn"
                                className="w-full sm:w-auto bg-[#15803D] hover:bg-[#166534] text-white">
                                {guardando ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                                Guardar suplementos para {fechaGuardado}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* LA NOTA, con las plantillas de Calma: el botón pone el texto en el campo. */}
            <div className="rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-3 space-y-2" data-testid="sup-nota">
                <Label htmlFor="sup-nota-texto" className="text-[#1E3A8A] text-sm">Nota para el cliente:</Label>
                <div className="flex flex-wrap gap-2">
                    {PLANTILLAS_DE_NOTA.map(p => (
                        <button key={p.id} onClick={() => setProtocolo(prev => ({ ...prev, nota: p.texto }))}
                            data-testid={`sup-plantilla-${p.id}`}
                            className="rounded bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs px-2.5 py-1">
                            Copiar «{p.titulo}»
                        </button>
                    ))}
                </div>
                <Textarea id="sup-nota-texto" value={protocolo.nota || ''} onChange={e => setProtocolo(p => ({ ...p, nota: e.target.value }))}
                    placeholder="Nota para el cliente…" className="bg-white border-[#D6D2C9] text-[#1C1B19]" rows={3} />
            </div>
        </div>
    );
};

export default SuplementosDelCliente;
